/**
 * JourneyBilling — the client-facing billing surface on the journey.
 *
 * Two pieces, deliberately OUTSIDE the main timeline (we don't put invoices in the celebratory
 * narrative):
 *   1. A new-invoice ACKNOWLEDGMENT popup — fires for any posted invoice the client hasn't cleared.
 *      They must acknowledge it; that records the clear, pings the firm in Slack, and files it away.
 *   2. A BILLING DRAWER — a quiet "Invoices" button (bottom-right) opening a slide-in panel that lists
 *      every issued invoice with paid/unpaid status and a Pay link on the unpaid ones.
 *
 * ONE pay link per invoice/statement (2026-09, Matt: clients should never see Stripe's own hosted
 * invoice page, and should pick bank vs. card on OUR checkout page, not via two separate links here)
 * — the link opens `/pay?t=<payToken>` (frontend/src/pages/PayPage.jsx), where the client picks a
 * payment method and pays inline. Status only shown here — no amounts, no totals (clients do their
 * own math); the fee-inclusive card total only appears once they're on the pay page. See
 * docs/STRIPE_PAYMENTS.md.
 *
 * Self-contained: loads its own data via services/journeyBilling.js; doesn't touch the journey flow.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { getJourneyBilling, acknowledgeInvoice } from '../services/journeyBilling.js'
import { getCachedPinProof } from '../services/journey.js'

const fmtDate = (d) => { if (!d) return ''; const dt = new Date(d); return isNaN(dt) ? '' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }

const BILLING_PERIOD_DAYS = 60

// Kill switch (2026-08-12, Matt): the automatic access-paused lockout (BillingHoldOverlay) is
// disabled firm-wide. Invoices, the acknowledgment popup, and the drawer are unaffected — only
// the hard block on an aged unpaid invoice is suppressed. Flip back to true to re-enable.
const AUTO_HOLD_ENABLED = false

// One pay link (2026-09) — opens our own checkout page, where the client chooses bank transfer or
// card, both at the same cost to them — see docs/STRIPE_PAYMENTS.md. `compact`
// is the dense inline style used in list rows / the popup; `full` is the stacked full-width button
// style used for "Pay full balance."
function PayLink({ url, compact, full }) {
  if (!url) return null
  if (full) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer"
         style={{ display: 'block', textAlign: 'center', fontFamily: '"DM Sans", sans-serif', fontSize: 14, fontWeight: 500, color: '#fff', background: '#07272D', borderRadius: 8, padding: '11px 0', textDecoration: 'none' }}>
        Pay invoice
      </a>
    )
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
       style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#1a4f8a', textDecoration: 'none', whiteSpace: 'nowrap' }}>
      Pay invoice
    </a>
  )
}

export default function JourneyBilling({ token, onBillingHold, openRef, onSummary }) {
  const [invoices, setInvoices] = useState([])
  const [pending, setPending]   = useState([])      // coreIds awaiting acknowledgment
  const [loaded, setLoaded]     = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [acking, setAcking]     = useState(false)
  const [statementPayUrl, setStatementPayUrl] = useState(null)
  // The project's CURRENT hourly rates (2026-11-03): { year, industryPartner, rows } or null.
  const [rates, setRates]       = useState(null)
  const [ratesOpen, setRatesOpen] = useState(false)

  // Read cached state synchronously so the pill appears on this visit without waiting for the API.
  const cached = (() => { try { const s = localStorage.getItem('journey-billing:' + token); return s ? JSON.parse(s) : {} } catch { return {} } })()

  // The parent hands us fresh inline arrow functions on every one of ITS renders. Holding them in
  // a ref, instead of naming them as dependencies of `load` below, is what stops this component
  // from re-fetching forever.
  //
  // The loop it closes (2026-09-12): load() calls onSummary with a newly-built object, the parent
  // stores it, storing it re-renders the parent, re-rendering mints new function identities, and a
  // dependency on those identities re-fires the effect, which fetches again. Nothing ever settles,
  // because the object is new on each pass and React can never bail out of the state update. One
  // open journey tab drove roughly 130 calls a minute to /api/journey-billing and 25,000 reads of
  // stripe_invoice_links in 24 minutes, until Supabase stopped accepting connections firm-wide.
  //
  // This was latent for months, held in check only by the endpoint being slow: every call used to
  // re-pull the whole Core invoice history (~19s) because the shared cache could not be written.
  // Repairing that cache removed the accidental brake and the loop ran at full speed. Treat "it
  // never caused trouble before" as no evidence at all that a loop like this is safe.
  const cbRef = useRef({ onBillingHold, onSummary })
  cbRef.current = { onBillingHold, onSummary }

  // A second, independent guard: never more than one request in flight, whatever re-renders occur.
  // The dependency fix above is the real repair; this bounds the damage if a later edit
  // reintroduces an unstable dependency.
  const inFlight = useRef(false)

  const load = useCallback(() => {
    if (!token) return
    if (inFlight.current) return
    inFlight.current = true
    getJourneyBilling(token)
      .then(d => {
        const ivs = d.invoices || []
        const payUrl = d.statementPayUrl || null
        setInvoices(ivs); setPending(d.pending || []); setStatementPayUrl(payUrl); setLoaded(true)
        const rc = d.rates && d.rates.rows && d.rates.rows.length ? d.rates : null
        setRates(rc)
        // "Updated" dot: only when a NEWER year's card arrives after they've seen one. The first
        // card a person ever sees is recorded quietly (no dot for rates that were always there).
        let ratesUpdated = false
        if (rc) {
          try {
            const seen = Number(localStorage.getItem('journey-rates-seen:' + token) || 0)
            if (!seen) localStorage.setItem('journey-rates-seen:' + token, String(rc.year))
            else ratesUpdated = rc.year > seen
          } catch { /* storage unavailable */ }
        }
        const now = Date.now()
        const hasOverdue = AUTO_HOLD_ENABLED && ivs.some(iv =>
          !iv.paid && iv.date &&
          (now - new Date(iv.date).getTime()) > BILLING_PERIOD_DAYS * 24 * 60 * 60 * 1000
        )
        // Persist hold + hasInvoices so the pill appears immediately on the next page load.
        try { localStorage.setItem('journey-billing:' + token, JSON.stringify({ held: hasOverdue, payUrl, hasInvoices: ivs.length > 0 })) } catch { /* storage unavailable */ }
        const { onBillingHold: notifyHold, onSummary: notifySummary } = cbRef.current
        if (notifyHold) notifyHold(hasOverdue, payUrl)
        const hasUnpaidNow = ivs.some(iv => !iv.paid)
        if (notifySummary) notifySummary({ hasUnpaid: hasUnpaidNow, hasInvoices: ivs.length > 0, hasRates: !!rc, ratesUpdated })
      })
      .catch(() => setLoaded(true))   // billing is non-critical to the journey — fail quiet
      .finally(() => { inFlight.current = false })
  }, [token])

  useEffect(() => { load() }, [load])

  // Wire openRef so JourneyShell can programmatically open the billing drawer.
  // openRef.current('rates') opens the rates window instead.
  useEffect(() => { if (openRef) openRef.current = (which) => (which === 'rates' ? openRates() : setDrawerOpen(true)) }, [openRef, rates])   // eslint-disable-line react-hooks/exhaustive-deps

  function openRates() {
    setDrawerOpen(false); setRatesOpen(true)
    if (rates) { try { localStorage.setItem('journey-rates-seen:' + token, String(rates.year)) } catch { /* storage unavailable */ } }
    const { onSummary: notifySummary } = cbRef.current
    if (notifySummary) notifySummary(prev => (prev && prev.ratesUpdated ? { ...prev, ratesUpdated: false } : prev))
  }

  // The first still-pending invoice drives the blocking popup.
  const pendingInvoice = invoices.find(iv => pending.includes(iv.coreId)) || null

  async function ackCurrent() {
    if (!pendingInvoice || acking) return
    setAcking(true)
    try {
      await acknowledgeInvoice(token, pendingInvoice.coreId)
      setPending(prev => prev.filter(id => id !== pendingInvoice.coreId))
      setInvoices(prev => prev.map(iv => iv.coreId === pendingInvoice.coreId ? { ...iv, acknowledged: true } : iv))
    } catch { /* leave it pending; they can try again */ }
    setAcking(false)
  }

  // Nothing issued yet (and no cached signal) → no billing UI at all.
  if (!loaded && !cached.hasInvoices) return null
  if (loaded && !invoices.length && !rates) return null

  const hasUnpaid = loaded ? invoices.some(iv => !iv.paid) : false

  return (
    <>
      {/* ── Acknowledgment popup (blocking) ── */}
      {pendingInvoice && (
        <div style={overlay}>
          <div style={card}>
            <div style={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 300, fontSize: 18, letterSpacing: '0.04em', color: '#07272D' }}>
              A new invoice has been issued
            </div>
            <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 14, color: '#3a5a60', marginTop: 10, lineHeight: 1.6 }}>
              Invoice <b>#{pendingInvoice.number || ''}</b>{pendingInvoice.date ? ' — ' + fmtDate(pendingInvoice.date) : ''} is now available.
              You can view and pay it any time from your Invoices.
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
              <PayLink url={statementPayUrl || pendingInvoice.payUrl} compact />
              <button onClick={ackCurrent} disabled={acking} style={primaryBtn}>
                {acking ? 'One moment…' : 'Dismiss'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Billing drawer ── */}
      {drawerOpen && (
        <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) setDrawerOpen(false) }}>
          <div style={drawer}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 300, fontSize: 16, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#07272D' }}>
                Invoices
              </div>
              <button onClick={() => setDrawerOpen(false)} style={{ background: 'none', border: 'none', fontSize: 20, color: '#9ab8bd', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            {/* "Pay full balance" when a statement link exists — clients pay everything at once. */}
            {statementPayUrl && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: '#6b8f95', marginBottom: 6, textAlign: 'center' }}>Pay full balance</div>
                <PayLink url={statementPayUrl} full />
              </div>
            )}
            {/* Capped + scrollable — some projects have 30+ invoices over their lifecycle. */}
            <div style={{ maxHeight: '56vh', overflowY: 'auto', margin: '0 -4px', padding: '0 4px' }}>
              {!loaded && <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#9ab8bd', padding: '18px 0', textAlign: 'center' }}>Loading invoices…</div>}
              {invoices.map(iv => (
                <div key={iv.coreId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '0.5px solid rgba(7,39,45,0.08)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 500, color: '#07272D' }}>Invoice #{iv.number || ''}</div>
                    <div style={{ fontFamily: '"DM Mono", monospace', fontSize: 11, color: '#6b8f95', marginTop: 2 }}>{fmtDate(iv.date)}</div>
                  </div>
                  <span style={{
                    fontFamily: '"DM Sans", sans-serif', fontSize: 10, fontWeight: 600, padding: '2px 9px', borderRadius: 20,
                    color: iv.paid ? '#1a7a4a' : '#B7651A', background: iv.paid ? '#eaf4ee' : '#fdf4e7',
                  }}>{iv.paid ? 'Paid' : 'Unpaid'}</span>
                  <a href={'/api/journey-invoice-pdf?t=' + encodeURIComponent(token) + '&invoiceId=' + encodeURIComponent(iv.coreId) + '&pinProof=' + encodeURIComponent(getCachedPinProof(token) || '')}
                     target="_blank" rel="noopener noreferrer" title="Download invoice PDF"
                     style={{ fontSize: 15, textDecoration: 'none', lineHeight: 1, color: '#6b8f95' }}>&#128196;</a>
                  {!statementPayUrl && !iv.paid && iv.payUrl && (
                    <PayLink url={iv.payUrl} compact />
                  )}
                </div>
              ))}
            </div>
            {rates && (
              <button onClick={openRates} style={ratesLink}>
                <span>Current hourly rates</span><span style={{ fontFamily: '"DM Mono", monospace', fontSize: 11, color: '#6b8f95' }}>View ›</span>
              </button>
            )}
            <div style={{ fontFamily: '”DM Sans”, sans-serif', fontSize: 11, color: '#9ab8bd', marginTop: 14, fontStyle: 'italic', lineHeight: 1.5 }}>
              {statementPayUrl ? 'Use “Pay full balance” above to settle everything at once.' : 'Each invoice opens its own secure payment page.'}{' '}
              Pay by bank transfer, card, Zelle or wire transfer.{' '}
              {hasUnpaid && 'Payments made online may take a day or two to show as paid.'}
            </div>
          </div>
        </div>
      )}

      {/* ── Current hourly rates ── */}
      {ratesOpen && rates && (
        <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) setRatesOpen(false) }}>
          <div style={drawer}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <div style={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 300, fontSize: 16, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#07272D' }}>
                Current hourly rates
                {rates.industryPartner && <span style={ipTag}>Industry Partner</span>}
              </div>
              <button onClick={() => setRatesOpen(false)} style={{ background: 'none', border: 'none', fontSize: 20, color: '#9ab8bd', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ fontFamily: '"DM Mono", monospace', fontSize: 11, color: '#6b8f95', margin: '4px 0 12px' }}>
              {rates.year} · effective January 1, {rates.year}
            </div>
            {rates.rows.map(r => (
              <div key={r.position} style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '8px 0', borderBottom: '0.5px solid rgba(7,39,45,0.08)' }}>
                <span style={{ flex: 1, fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#07272D' }}>{r.position}</span>
                <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 12.5, color: '#07272D', fontVariantNumeric: 'tabular-nums' }}>{fmtRate(r.rate)}</span>
              </div>
            ))}
            <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: '#9ab8bd', marginTop: 14, fontStyle: 'italic', lineHeight: 1.5 }}>
              {rates.industryPartner && 'Your project is billed at our Industry Partner rate, which we reserve for trusted collaborators in architecture, design, and construction. '}
              Hourly work on your project is billed at these rates. As stated in your agreement, hourly rates are subject to change on an annual basis.
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const fmtRate = (n) => '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const ratesLink = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginTop: 12,
  padding: '10px 12px', borderRadius: 8, border: 'none', background: '#f4f7f8', cursor: 'pointer',
  fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: '#1a4f8a', textAlign: 'left',
}
const ipTag = {
  display: 'inline-block', fontFamily: '"DM Sans", sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: 0,
  textTransform: 'none', padding: '2px 9px', borderRadius: 20, color: '#7a5a1e', background: '#f6efe1', marginLeft: 8, verticalAlign: 2,
}

const overlay = {
  position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(7,39,45,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
}
const card = {
  background: '#fff', borderRadius: 12, padding: '22px 24px', width: 440, maxWidth: '94vw',
  boxShadow: '0 12px 40px rgba(7,39,45,0.3)',
}
const drawer = {
  background: '#fff', borderRadius: 12, padding: '20px 22px', width: 460, maxWidth: '94vw',
  maxHeight: '82vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(7,39,45,0.3)',
}
const primaryBtn = {
  fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 500, color: '#fff',
  background: '#07272D', border: 'none', borderRadius: 6, padding: '8px 18px', cursor: 'pointer',
}
